import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from './shared.service';
import { Utils } from './utils';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'employers',
  templateUrl: './employers.component.html',
  styleUrls: ['./employers.component.scss'],
  standalone: false,
})
export class EmployersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('openingSplitHero') openingSplitHero?: ElementRef<HTMLElement>;
  @ViewChild('openingCtaButton') openingCtaButton?: ElementRef<HTMLElement>;
  @ViewChild('openingImageWrapper') openingImageWrapper?: ElementRef<HTMLElement>;

  email = Utils.email;
  phoneCall = Utils.phoneCall;
  phoneNumber = Utils.phoneNumber;
  openingImageHeight: number | null = null;

  public constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private titleService: Title,
    private sharedService: SharedService,
    private readonly seo: SeoService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.titleService.setTitle('Blauwtand — Voor opdrachtgevers');
    this.seo.updateSeo({
      title: 'IT Recruitmentoplossing voor Opdrachtgevers | Blauwtand',
      description:
        'IT diensten voor opdrachtgevers. Vind snel de juiste IT professionals via Blauwtand, jouw recruitmentpartner voor opdrachtgevers. Bekijk meer.',
    });
  }

  private intersectionObserver?: IntersectionObserver;
  private kickHandler?: () => void;
  private prefersReducedMotion = false;

  public async ngAfterViewInit(): Promise<void> {
    this.prefersReducedMotion = this.getPrefersReducedMotion();

    // Defer until layout has stabilized (iOS Safari can delay initial IntersectionObserver callbacks until a user gesture).
    await this.waitForAnimationFrames(2);
    this.setupScrollAnimations();
    this.updateOpeningAlignmentDebugLines();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateOpeningAlignmentDebugLines();
  }

  public navigateTo(menuItem: string, isMobileMenu: boolean = false) {
    if (menuItem) {
      this.router.navigate([`../${menuItem.toLowerCase()}`], {
        relativeTo: this.activatedRoute,
      });
    }
  }

  private setupScrollAnimations(): void {
    const animatedElements = Array.from(
      document.querySelectorAll<HTMLElement>('.animate-on-scroll')
    );

    if (animatedElements.length === 0) {
      return;
    }

    // Reduced motion: never hide content and skip observers.
    if (this.prefersReducedMotion) {
      for (const el of animatedElements) {
        el.classList.remove('animate-hidden');
      }
      return;
    }

    // If IntersectionObserver isn't available, fall back to immediately showing everything.
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      for (const el of animatedElements) {
        el.classList.remove('animate-hidden');
      }
      return;
    }

    this.intersectionObserver?.disconnect();

    // Create the observer once; we'll attach elements after we do an initial "reveal if already visible" pass.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          entry.target.classList.remove('animate-hidden');
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    this.intersectionObserver = observer;

    // Safari/iOS quirk: initial IO callbacks can be delayed until a scroll/resize.
    // Also: removing the class in the same frame as layout can prevent transitions.
    // So we do the initial reveal + observe work on the next animation frame.
    requestAnimationFrame(() => {
      this.revealVisibleElements(animatedElements);

      const remainingElements = animatedElements.filter((el) =>
        el.classList.contains('animate-hidden')
      );

      for (const el of remainingElements) {
        observer.observe(el);
      }

      // One-time "kick": if callbacks are still delayed, a scroll/resize/orientation change will reveal.
      this.installKickReveal(animatedElements);
    });
  }

  private revealVisibleElements(elements: HTMLElement[]): void {
    // Keep the logic in sync with IO rootMargin bottom (-60px).
    const bottomOffsetPx = 60;
    const viewportHeight = window.innerHeight || 0;

    for (const el of elements) {
      if (!el.classList.contains('animate-hidden')) {
        continue;
      }

      const rect = el.getBoundingClientRect();
      const isInView = rect.top < viewportHeight - bottomOffsetPx && rect.bottom > 0;

      if (isInView) {
        el.classList.remove('animate-hidden');
      }
    }
  }

  private installKickReveal(elements: HTMLElement[]): void {
    this.removeKickReveal();

    const handler = () => {
      this.revealVisibleElements(elements);

      const anyHiddenLeft = elements.some((el) => el.classList.contains('animate-hidden'));
      if (!anyHiddenLeft) {
        this.removeKickReveal();
      }
    };

    this.kickHandler = handler;

    // Run immediately and after a short delay (covers late layout/font/image settling).
    setTimeout(handler, 0);
    setTimeout(handler, 250);

    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('orientationchange', handler);
  }

  private removeKickReveal(): void {
    if (!this.kickHandler) {
      return;
    }

    window.removeEventListener('scroll', this.kickHandler);
    window.removeEventListener('resize', this.kickHandler);
    window.removeEventListener('orientationchange', this.kickHandler);
    this.kickHandler = undefined;
  }

  private getPrefersReducedMotion(): boolean {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    } catch {
      return false;
    }
  }

  private waitForAnimationFrames(frameCount: number): Promise<void> {
    const frames = Math.max(1, Math.floor(frameCount));

    return new Promise((resolve) => {
      let remaining = frames;
      const step = () => {
        remaining -= 1;
        if (remaining <= 0) {
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    });
  }

  public ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.removeKickReveal();
  }

  private updateOpeningAlignmentDebugLines(): void {
    requestAnimationFrame(() => {
      const splitHero = this.openingSplitHero?.nativeElement;
      const ctaButton = this.openingCtaButton?.nativeElement;
      const imageWrapper = this.openingImageWrapper?.nativeElement;

      if (!splitHero || !ctaButton || !imageWrapper) {
        this.openingImageHeight = null;
        this.cdr.markForCheck();
        return;
      }

      if (window.innerWidth <= 1024) {
        this.openingImageHeight = null;
        this.cdr.markForCheck();
        return;
      }

      const splitRect = splitHero.getBoundingClientRect();
      const buttonRect = ctaButton.getBoundingClientRect();
      const imageRect = imageWrapper.getBoundingClientRect();

      this.openingImageHeight = Math.max(
        buttonRect.bottom - splitRect.top - (imageRect.top - splitRect.top),
        320,
      );
      this.cdr.markForCheck();
    });
  }
}
